import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider } from '@ucl/fpds/themeProvider';
import { Button } from '@ucl/fpds/button';
import { Checkbox } from '@ucl/fpds/checkbox';
import { Drawer } from '@ucl/fpds/drawer';
import { Pagination } from '@ucl/fpds/pagination';
import { Icon } from '@ucl/fpds/icons';
import { Spinner } from '@ucl/fpds/spinner';
import { fetchServiceHistoryPayload, unwrapServiceHistoryResponse, } from '../../graphql/serviceHistoryApi';
import { FeatureFlagsRoot, initializeFeatureFlags } from '../../featureFlags';
import '../../styles.css';
import { AMPLITUDE_EVENTS, initializeAmplitudeSdk, trackAmplitudeEvent } from '../../utils/amplitudeUtils';
import { formatCostValue } from '../../utils/currencyUtils';
import { formatDateValue } from '../../utils/dateUtils';
import { useRuntimeEnvStatus } from '../../useRuntimeEnv';
const SHOW_DOWNLOAD_SERVICE_HISTORY = false;
// Maps sortable UI columns to the field names the BE API expects for the sortBy param.
const SORT_FIELD_MAP = {
    serviceHistoryNo: 'repairOrderId',
    date: 'repairOrderCloseDate',
    odometerMiles: 'odometerReading',
    engineHours: 'engineHours',
};
const NULL_DISPLAY = '—';
function formatCellValue(value, fallback = NULL_DISPLAY) {
    if (value === null || value === undefined) {
        return fallback;
    }
    const asString = String(value).trim();
    return asString.length > 0 ? asString : fallback;
}
function formatDataSourceLabel(value) {
    const source = formatCellValue(value).toLowerCase();
    if (source === 'dcds')
        return 'Ford Dealership';
    if (source === 'fleetio')
        return 'Ford Fleet Management';
    if (source === 'telematics')
        return 'Manually Added';
    return formatCellValue(value);
}
export function mapGraphqlServiceHistoryResponse(payload) {
    const list = unwrapServiceHistoryResponse(payload).items;
    return list.map((item) => {
        const row = (item ?? {});
        const yearPart = row.vehicleYear ? String(row.vehicleYear).trim() : '';
        const makePart = row.vehicleMake ? String(row.vehicleMake).trim() : '';
        const modelPart = row.vehicleModel ? String(row.vehicleModel).trim() : '';
        const vehicleNameParts = [yearPart, makePart, modelPart].filter(Boolean);
        const graphqlVehicleName = formatCellValue(row.vehicleName, '');
        return {
            serviceHistoryNo: formatCellValue(row.repairOrderId).replace(/^\[|\]$/g, '').trim(),
            vin: formatCellValue(row.vin),
            vehicleId: formatCellValue(row.vehicleId, ''),
            vehicleName: graphqlVehicleName ||
                (vehicleNameParts.length > 0 ? vehicleNameParts.join(' ') : formatCellValue(row.vin)),
            dataSource: formatDataSourceLabel(row.dataSource),
            url: formatCellValue(row.url),
            date: formatDateValue(row.repairOrderCloseDate),
            odometerMiles: formatCellValue(row.odometerReading),
            engineHours: formatCellValue(row.engineHours),
            serviceCenter: formatCellValue(row.repairShopName, NULL_DISPLAY),
            issuesAddressed: formatCellValue(row.serviceDescription),
            serviceCategory: formatCellValue(row.serviceDescription),
            laborCost: formatCellValue(row.laborTotalAmount),
            partsCost: formatCellValue(row.partsTotalAmount),
            totalCost: formatCellValue(row.totalAmount),
        };
    });
}
export function buildServiceHistoryCsv(rows) {
    const headers = [
        'Service History No',
        'VIN',
        'Date',
        'Odometer(mi)',
        'Engine Hours',
        'Service Center',
        'Service Categories',
        'Total Cost',
    ];
    const escape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const contentRows = rows.map((row) => [
        row.serviceHistoryNo,
        row.vin,
        row.date,
        row.odometerMiles,
        row.engineHours,
        row.serviceCenter,
        row.serviceCategory,
        row.totalCost,
    ]
        .map(escape)
        .join(','));
    return [headers.join(','), ...contentRows].join('\n');
}
async function fetchServiceHistoryRows(params, offset = 0, hostApp, sortConfig, segmentId) {
    const page = unwrapServiceHistoryResponse(await fetchServiceHistoryPayload({
        ...params,
        limit: 100,
        offset,
        sortBy: sortConfig ? SORT_FIELD_MAP[sortConfig.column] : undefined,
        sortDirection: sortConfig?.direction,
    }, { hostApp, segmentId }));
    return {
        rows: mapGraphqlServiceHistoryResponse({ items: page.items }),
        totalCount: typeof page.totalCount === 'number' ? page.totalCount : page.items.length,
    };
}
function renderCell(value) {
    return value === NULL_DISPLAY ? _jsx("span", { className: "shvev-null-dash", children: NULL_DISPLAY }) : value;
}
const COLUMNS = [
    { key: 'serviceHistoryNo', label: 'Service History No', sortable: true },
    { key: 'date', label: 'Date', sortable: true },
    { key: 'odometerMiles', label: 'Odometer(mi)', sortable: true },
    { key: 'engineHours', label: 'Engine Hours', sortable: true },
    { key: 'serviceCenter', label: 'Service Center', sortable: false },
    { key: 'serviceCategory', label: 'Service Categories', sortable: false },
    { key: 'totalCost', label: 'Total Cost', sortable: false },
];
function ServiceHistoryVehicleExpandedView({ hostApp, segmentId, vin, repairOrderId, ffmCompanyId, }) {
    useEffect(() => { initializeAmplitudeSdk(); }, []);
    const [rows, setRows] = useState([]);
    const [loadedWindowStart, setLoadedWindowStart] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const totalCountRef = useRef(0);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [retryRequestVersion, setRetryRequestVersion] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState({ column: 'date', direction: 'desc' });
    const [drawerRow, setDrawerRow] = useState(null);
    const [downloadToast, setDownloadToast] = useState(null);
    const toastTimeoutRef = useRef(null);
    const toastShowingRef = useRef(false);
    const downloadInFlightRef = useRef(false);
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [expandedRows, setExpandedRows] = useState(new Set());
    const loadedWindowRef = useRef(null);
    const loadedScopeRef = useRef('');
    const requestSequenceRef = useRef(0);
    const dismissDownloadToast = () => {
        setDownloadToast(null);
        toastShowingRef.current = false;
        if (toastTimeoutRef.current !== null) {
            window.clearTimeout(toastTimeoutRef.current);
            toastTimeoutRef.current = null;
        }
    };
    const showDownloadToast = (message, tone) => {
        // Use ref for synchronous guard — prevents double-invoke
        if (toastShowingRef.current) {
            return;
        }
        if (toastTimeoutRef.current !== null) {
            window.clearTimeout(toastTimeoutRef.current);
        }
        toastShowingRef.current = true;
        setDownloadToast({ message, tone });
        toastTimeoutRef.current = window.setTimeout(() => {
            dismissDownloadToast();
        }, 5000);
    };
    useEffect(() => {
        let isMounted = true;
        const scopeKey = `${hostApp ?? ''}|${segmentId ?? ''}|${vin ?? ''}|${repairOrderId ?? ''}|${ffmCompanyId ?? ''}|${sortConfig?.column ?? ''}|${sortConfig?.direction ?? ''}`;
        const fetchServiceHistory = async () => {
            const isNewScope = loadedScopeRef.current !== scopeKey;
            if (isNewScope) {
                loadedScopeRef.current = scopeKey;
                loadedWindowRef.current = null;
                setRows([]);
                totalCountRef.current = 0;
                setTotalCount(0);
                setLoadedWindowStart(0);
                setCurrentPage(1);
            }
            const requestedOffset = isNewScope ? 0 : (currentPage - 1) * rowsPerPage;
            const requestedEnd = requestedOffset + rowsPerPage;
            const loadedWindow = loadedWindowRef.current;
            const knownTotalCount = totalCountRef.current;
            const isFinalPage = knownTotalCount > 0 && requestedEnd >= knownTotalCount;
            const hasRequestedRange = loadedWindow?.scopeKey === scopeKey &&
                requestedOffset >= loadedWindow.start &&
                (requestedEnd <= loadedWindow.start + loadedWindow.length ||
                    (isFinalPage && loadedWindow.start + loadedWindow.length >= knownTotalCount));
            if (hasRequestedRange)
                return;
            const requestId = requestSequenceRef.current + 1;
            requestSequenceRef.current = requestId;
            setIsLoading(true);
            setErrorMessage('');
            try {
                const result = await fetchServiceHistoryRows({ vin, repairOrderId, ffmCompanyId }, requestedOffset, hostApp, sortConfig, segmentId);
                if (!isMounted || requestId !== requestSequenceRef.current || loadedScopeRef.current !== scopeKey) {
                    return;
                }
                loadedWindowRef.current = { start: requestedOffset, length: result.rows.length, scopeKey };
                setRows(result.rows);
                setLoadedWindowStart(requestedOffset);
                totalCountRef.current = result.totalCount;
                setTotalCount(result.totalCount);
                trackAmplitudeEvent(AMPLITUDE_EVENTS.VEHICLE_EXPANDED_FETCH_SUCCEEDED, {
                    rowCount: result.rows.length,
                    vin: vin ?? null,
                    repairOrderId: repairOrderId ?? null,
                    ffmCompanyId: ffmCompanyId ?? null,
                });
            }
            catch {
                if (!isMounted) {
                    return;
                }
                loadedWindowRef.current = null;
                setRows([]);
                totalCountRef.current = 0;
                setTotalCount(0);
                setErrorMessage('Unable to load service history right now.');
                trackAmplitudeEvent(AMPLITUDE_EVENTS.VEHICLE_EXPANDED_FETCH_FAILED, {
                    vin: vin ?? null,
                    repairOrderId: repairOrderId ?? null,
                    ffmCompanyId: ffmCompanyId ?? null,
                });
            }
            finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };
        void fetchServiceHistory();
        return () => {
            isMounted = false;
        };
    }, [hostApp, segmentId, vin, repairOrderId, ffmCompanyId, currentPage, rowsPerPage, sortConfig, retryRequestVersion]);
    useEffect(() => {
        return () => {
            if (toastTimeoutRef.current !== null) {
                window.clearTimeout(toastTimeoutRef.current);
            }
        };
    }, []);
    const filteredRows = rows;
    // BE API call performs sorting; server response order is used as-is.
    const sortedRows = filteredRows;
    const paginatedRows = useMemo(() => {
        const startIndex = Math.max(0, (currentPage - 1) * rowsPerPage - loadedWindowStart);
        const endIndex = startIndex + rowsPerPage;
        return sortedRows.slice(startIndex, endIndex);
    }, [sortedRows, currentPage, rowsPerPage, loadedWindowStart]);
    const handleDownloadCsv = async () => {
        if (downloadInFlightRef.current) {
            return;
        }
        downloadInFlightRef.current = true;
        trackAmplitudeEvent(AMPLITUDE_EVENTS.VEHICLE_EXPANDED_DOWNLOAD_CLICKED, {
            vin: vin ?? null,
            repairOrderId: repairOrderId ?? null,
            ffmCompanyId: ffmCompanyId ?? null,
        });
        try {
            const csvContent = buildServiceHistoryCsv(sortedRows);
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const downloadUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', 'service-history.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(downloadUrl);
            showDownloadToast('Service History downloaded successfully', 'success');
            trackAmplitudeEvent(AMPLITUDE_EVENTS.VEHICLE_EXPANDED_DOWNLOAD_SUCCEEDED, {
                exportedRowCount: sortedRows.length,
                vin: vin ?? null,
                repairOrderId: repairOrderId ?? null,
                ffmCompanyId: ffmCompanyId ?? null,
            });
        }
        catch {
            showDownloadToast("Couldn't download Service History", 'error');
        }
        finally {
            downloadInFlightRef.current = false;
            trackAmplitudeEvent(AMPLITUDE_EVENTS.VEHICLE_EXPANDED_DOWNLOAD_FAILED, {
                vin: vin ?? null,
                repairOrderId: repairOrderId ?? null,
                ffmCompanyId: ffmCompanyId ?? null,
            });
        }
    };
    const toggleRowSelected = (key) => {
        setSelectedRows((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            }
            else {
                next.add(key);
            }
            return next;
        });
    };
    const toggleRowExpanded = (key) => {
        // If the toggled row is among the selected rows, expand/collapse all selected rows together
        setExpandedRows((prev) => {
            const keysToToggle = selectedRows.has(key)
                ? Array.from(selectedRows)
                : [key];
            const willExpand = !prev.has(key);
            const next = new Set(prev);
            for (const k of keysToToggle) {
                if (willExpand) {
                    next.add(k);
                }
                else {
                    next.delete(k);
                }
            }
            return next;
        });
    };
    const allPageSelected = paginatedRows.length > 0 && paginatedRows.every((r) => selectedRows.has(r.serviceHistoryNo));
    const somePageSelected = paginatedRows.some((r) => selectedRows.has(r.serviceHistoryNo));
    const showPaginationControls = totalCount >= 10;
    const showNoResults = !isLoading && (Boolean(errorMessage) || rows.length === 0);
    const retryLoadServiceHistory = (event) => {
        event.preventDefault();
        setRetryRequestVersion((version) => version + 1);
    };
    const toggleSelectAllPage = () => {
        if (allPageSelected) {
            setSelectedRows(new Set());
        }
        else {
            setSelectedRows(new Set(paginatedRows.map((r) => r.serviceHistoryNo)));
        }
    };
    return (_jsxs(ThemeProvider, { productTeamName: "rpst-sm-unified-service-ui", children: [_jsxs("section", { "aria-label": "service history vehicle expanded view", className: "shvev-root", children: [_jsxs("div", { className: "shvev-controls", children: [errorMessage && (_jsxs("div", { className: "shvev-fleet-error-alert", role: "alert", children: [_jsx("span", { className: "shvev-fleet-error-icon", "aria-hidden": "true", children: _jsxs("svg", { viewBox: "0 0 24 24", width: "24", height: "24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("path", { d: "M12 3L22 20H2L12 3Z", fill: "currentColor" }), _jsx("text", { x: "12", y: "17", textAnchor: "middle", fill: "#ffffff", fontSize: "14", fontWeight: "700", fontFamily: "Inter, sans-serif", children: "!" })] }) }), _jsxs("span", { children: ["Couldn't load service history,", ' ', _jsx("a", { href: window.location.href, onClick: retryLoadServiceHistory, children: "try again" })] })] })), _jsx("div", { className: "shvev-controls-top shvev-controls-top--left", children: SHOW_DOWNLOAD_SERVICE_HISTORY && (_jsxs(Button, { variant: "secondary", className: "shvev-download-btn", disabled: showNoResults, onClick: handleDownloadCsv, children: [_jsx(Icon, { icon: "download", size: "s" }), "Download Service History"] })) })] }), isLoading && (_jsx("div", { className: "shvev-loading", "aria-live": "polite", children: _jsx(Spinner, { size: "medium", role: "status", "aria-label": "Loading service history" }) })), !isLoading && (_jsxs(_Fragment, { children: [showNoResults && (_jsx("div", { className: "shvev-no-results", role: "status", "aria-live": "polite", children: "No Results" })), !showNoResults && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "shvev-table-shell", children: [_jsxs("table", { className: "shvev-table", children: [_jsx("thead", { children: _jsx("tr", { children: COLUMNS.map((column) => {
                                                                const isActive = sortConfig?.column === column.key;
                                                                const isSortable = column.sortable;
                                                                return (_jsx("th", { scope: "col", "aria-sort": isSortable && isActive ? (sortConfig?.direction === 'asc' ? 'ascending' : 'descending') : undefined, children: isSortable ? (_jsxs("button", { type: "button", className: "shvev-th-button", onClick: () => {
                                                                            const nextDirection = isActive && sortConfig?.direction === 'asc' ? 'desc' : 'asc';
                                                                            setSortConfig({ column: column.key, direction: nextDirection });
                                                                            setCurrentPage(1);
                                                                        }, "aria-label": `Sort by ${column.label}${isActive ? ` (${sortConfig?.direction === 'asc' ? 'ascending' : 'descending'})` : ''}`, children: [column.label, isActive ? (_jsx("span", { className: "shvev-sort-icon--active", "aria-hidden": "true", children: sortConfig?.direction === 'asc' ? '▲' : '▼' })) : (_jsxs("span", { className: "shvev-sort-icon", "aria-hidden": "true", children: [_jsx("span", { className: "shvev-sort-tri", children: "\u25B2" }), _jsx("span", { className: "shvev-sort-tri", children: "\u25BC" })] }))] })) : (column.label) }, column.key));
                                                            }) }) }), _jsx("tbody", { children: paginatedRows.map((row) => (_jsxs("tr", { children: [_jsxs("td", { children: [_jsx(Button, { variant: "tertiary", className: "shvev-service-link-btn", onClick: () => setDrawerRow(row), "aria-label": `View details for ${row.serviceHistoryNo}`, children: row.serviceHistoryNo }), _jsx("span", { className: "shvev-source-subtext", children: row.dataSource })] }), _jsx("td", { children: renderCell(row.date) }), _jsx("td", { children: renderCell(row.odometerMiles) }), _jsx("td", { children: renderCell(row.engineHours) }), _jsx("td", { children: renderCell(row.serviceCenter) }), _jsx("td", { children: renderCell(row.serviceCategory) }), _jsx("td", { children: renderCell(formatCostValue(row.totalCost)) })] }, row.serviceHistoryNo))) })] }), sortedRows.length === 0 && _jsx("p", { className: "shvev-empty", children: "No service history records found." })] }), showPaginationControls && (_jsxs("div", { className: "shvev-pagination shvev-pagination--desktop", children: [_jsxs("div", { className: "shvev-pagination-left", children: [_jsx(Pagination, { totalItems: totalCount, itemsPerPage: rowsPerPage, page: currentPage, showPerPageOption: true, showTotalItems: false, exceedOverflow: true, hidePageListButtons: true, contentAlignment: "left", perPageOptions: [10, 20, 30, 40, 50, 100], initialItemsPerPage: rowsPerPage, onItemsPerPageChange: (val) => {
                                                            setRowsPerPage(val);
                                                            setCurrentPage(1);
                                                        } }), _jsxs("span", { className: "shvev-pagination-total", children: [totalCount, " Total"] })] }), _jsx(Pagination, { totalItems: totalCount, itemsPerPage: rowsPerPage, page: currentPage, onPageChange: (page) => setCurrentPage(page), showPerPageOption: false, showTotalItems: false, contentAlignment: "right" })] })), _jsxs("div", { className: "shvev-mobile-list", role: "region", "aria-label": "service history records", children: [_jsx("div", { className: "shvev-mobile-list-header", children: _jsx(Checkbox, { label: "Service History", checkboxValue: allPageSelected ? 2 : somePageSelected ? 1 : 0, onChange: () => toggleSelectAllPage() }) }), paginatedRows.map((row) => {
                                                const isExpanded = expandedRows.has(row.serviceHistoryNo);
                                                const isSelected = selectedRows.has(row.serviceHistoryNo);
                                                return (_jsxs("div", { className: `shvev-mobile-item${isExpanded ? ' shvev-mobile-item--expanded' : ''}`, children: [_jsxs("div", { className: "shvev-mobile-item-header", children: [_jsx("div", { className: "shvev-mobile-checkbox-wrap", children: _jsx(Checkbox, { label: "", "aria-label": `Select ${row.serviceHistoryNo}`, checkboxValue: isSelected ? 2 : 0, onChange: () => toggleRowSelected(row.serviceHistoryNo) }) }), _jsxs("div", { className: "shvev-mobile-item-info", children: [_jsx(Button, { variant: "tertiary", className: "shvev-mobile-service-no", onClick: () => setDrawerRow(row), "aria-label": `View details for ${row.serviceHistoryNo}`, children: row.serviceHistoryNo }), _jsx("span", { className: "shvev-mobile-source", children: row.dataSource })] }), _jsxs("div", { className: "shvev-mobile-item-actions", children: [_jsx(Button, { variant: "tertiary", className: "shvev-mobile-expand-btn", onClick: () => toggleRowExpanded(row.serviceHistoryNo), "aria-label": isExpanded ? `Collapse ${row.serviceHistoryNo}` : `Expand ${row.serviceHistoryNo}`, "aria-expanded": isExpanded, children: _jsx(Icon, { icon: isExpanded ? 'caretUp' : 'caretDown', size: "m" }) }), _jsx(Button, { variant: "tertiary", className: "shvev-mobile-kebab-btn", "aria-label": `More options for ${row.serviceHistoryNo}`, children: _jsx(Icon, { icon: "overflowMenuVertical", size: "m" }) })] })] }), isExpanded && (_jsxs("div", { className: "shvev-mobile-item-body", children: [_jsxs("div", { className: "shvev-mobile-detail-row", children: [_jsx("span", { className: "shvev-mobile-detail-label", children: "Date" }), _jsx("span", { className: "shvev-mobile-detail-value", children: row.date })] }), _jsxs("div", { className: "shvev-mobile-detail-row", children: [_jsx("span", { className: "shvev-mobile-detail-label", children: "Odometer (mi)" }), _jsx("span", { className: "shvev-mobile-detail-value", children: row.odometerMiles })] }), _jsxs("div", { className: "shvev-mobile-detail-row", children: [_jsx("span", { className: "shvev-mobile-detail-label", children: "Engine Hours" }), _jsx("span", { className: "shvev-mobile-detail-value", children: row.engineHours })] }), _jsxs("div", { className: "shvev-mobile-detail-row", children: [_jsx("span", { className: "shvev-mobile-detail-label", children: "Service Center" }), _jsx("span", { className: "shvev-mobile-detail-value", children: row.serviceCenter })] }), _jsxs("div", { className: "shvev-mobile-detail-row", children: [_jsx("span", { className: "shvev-mobile-detail-label", children: "Service Categories" }), _jsx("span", { className: "shvev-mobile-detail-value", children: row.serviceCategory })] }), _jsxs("div", { className: "shvev-mobile-detail-row", children: [_jsx("span", { className: "shvev-mobile-detail-label", children: "Total Cost" }), _jsx("span", { className: "shvev-mobile-detail-value", children: row.totalCost })] })] }))] }, row.serviceHistoryNo));
                                            }), sortedRows.length === 0 && _jsx("p", { className: "shvev-empty", children: "No service history records found." }), showPaginationControls && (_jsxs("div", { className: "shvev-pagination", children: [_jsxs("div", { className: "shvev-pagination-left", children: [_jsx(Pagination, { totalItems: totalCount, itemsPerPage: rowsPerPage, page: currentPage, showPerPageOption: true, showTotalItems: false, exceedOverflow: true, hidePageListButtons: true, contentAlignment: "left", perPageOptions: [10, 20, 30, 40, 50, 100], initialItemsPerPage: rowsPerPage, onItemsPerPageChange: (val) => {
                                                                    setRowsPerPage(val);
                                                                    setCurrentPage(1);
                                                                } }), _jsxs("span", { className: "shvev-pagination-total", children: [totalCount, " Total"] })] }), _jsx(Pagination, { totalItems: totalCount, itemsPerPage: rowsPerPage, page: currentPage, onPageChange: (page) => setCurrentPage(page), showPerPageOption: false, showTotalItems: false, contentAlignment: "right" })] }))] })] }))] })), downloadToast && (_jsx("div", { className: "shvev-snackbar-wrapper", children: _jsxs("div", { className: `shvev-toast shvev-toast--${downloadToast.tone}`, role: "alert", "aria-live": "assertive", children: [_jsx("span", { className: "shvev-toast-message", children: downloadToast.tone === 'error' ? "Couldn't download Service History." : 'Service History downloaded successfully.' }), _jsx("button", { className: "shvev-toast-close", "aria-label": "Dismiss notification", onClick: dismissDownloadToast, children: _jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", "aria-hidden": "true", children: _jsx("path", { d: "M12 4L4 12M4 4l8 8", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" }) }) })] }) }))] }), _jsxs(Drawer, { isOpen: drawerRow !== null, onClose: () => setDrawerRow(null), placement: "right", size: "medium", children: [_jsx(Drawer.Header, { titleProp: { title: drawerRow?.serviceHistoryNo ?? '', headingLevel: 'h2' }, onClose: () => setDrawerRow(null) }), _jsx(Drawer.Content, { children: drawerRow !== null && (_jsxs("div", { className: "shvev-drawer-body", children: [_jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Date" }), _jsx("span", { className: "shvev-drawer-value", children: renderCell(drawerRow.date) })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Odometer" }), _jsxs("span", { className: "shvev-drawer-value", children: [renderCell(drawerRow.odometerMiles), " mi"] })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Engine Hours" }), _jsx("span", { className: "shvev-drawer-value", children: renderCell(drawerRow.engineHours) })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Service Center" }), _jsx("span", { className: "shvev-drawer-value", children: renderCell(drawerRow.serviceCenter) })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Service Categories" }), _jsx("span", { className: "shvev-drawer-value", children: renderCell(drawerRow.serviceCategory) })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Issues Addressed" }), _jsx("span", { className: "shvev-drawer-value", children: renderCell(drawerRow.issuesAddressed) })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Labor" }), _jsxs("span", { className: "shvev-drawer-value", children: ["Cost: ", renderCell(drawerRow.laborCost), " USD"] })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Parts" }), _jsxs("span", { className: "shvev-drawer-value", children: ["Cost: ", renderCell(drawerRow.partsCost), " USD"] })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Total Cost" }), _jsxs("span", { className: "shvev-drawer-value", children: [renderCell(formatCostValue(drawerRow.totalCost)), " USD"] })] })] })) }), _jsx(Drawer.Footer, { children: _jsx(Button, { variant: "secondary", onClick: () => setDrawerRow(null), children: "Close" }) })] })] }));
}
function ServiceHistoryVehicleExpandedViewWrapper(props) {
    const runtimeEnvStatus = useRuntimeEnvStatus();
    useEffect(() => {
        if (runtimeEnvStatus === 'ready')
            initializeFeatureFlags();
    }, [runtimeEnvStatus]);
    if (runtimeEnvStatus === 'loading') {
        return (_jsx("div", { className: "shvev-loading", "aria-live": "polite", children: _jsx(Spinner, { size: "medium", role: "status", "aria-label": "Loading service history" }) }));
    }
    if (runtimeEnvStatus === 'error') {
        return _jsx("p", { role: "alert", children: "Unable to load Service Management configuration." });
    }
    return (_jsx(FeatureFlagsRoot, { children: _jsx(ServiceHistoryVehicleExpandedView, { ...props }) }));
}
export default ServiceHistoryVehicleExpandedViewWrapper;
